import React, { useState } from 'react'
import { useFocusEffect } from '@react-navigation/native'
import { View, Image, SafeAreaView, Alert, Platform } from 'react-native'
import * as Linking from 'expo-linking'
import SegmentedControlTab from 'react-native-segmented-control-tab'
import { useSelector, useDispatch } from 'react-redux'
import tw from 'tailwind-rn'
// import { CommonActions, StackActions } from '@react-navigation/native'

import ActionCreators from '../../store/actions'

import styles from './styles'
import {
  HGText,
  HGView,
  HGHeading,
  HGButton,
  HGSectionList,
} from '../../components/HGUI'
import VersionInfo from '../../components/VersionInfo'
import patientGroups from '../../config/patientGroups'
import CrossPlatformImage from '../../components/CrossPlatformImage'

type Props = {
  route: any
  navigation: any
}

const SettingsOverviewScreen = (props: Props) => {
  const { navigation, route } = props
  const dispatch = useDispatch()
  const { patientGroup, email, emailVerified } = useSelector(
    (state) => state.user
  )
  const [hasRequestedVerify, setHasRequestedVerify] = useState<boolean>(
    emailVerified
  )
  const [hideInfoMessages, setHideInfoMessages] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<{ message: string } | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

  const handleIndexChange = (index: number) => {
    setSelectedIndex(index)

    const patientGroupData = patientGroups[index]

    dispatch(
      ActionCreators.setPatientGroup({ patientGroup: patientGroupData.id })
    )
      .then(() => {
        navigation.reset({
          routes: [{ name: 'SignedIn' }],
        })

        Alert.alert(
          'Patientengruppe geändert',
          `Patientengruppe ist jetzt ${patientGroupData.label}.`
        )
        // navigation.replace('SettingsOverview')
      })
      .catch((/* reason */) => {})
  }

  const handleLogout = () => {
    if (Platform.OS === 'web') {
      dispatch(ActionCreators.logoutUser())
      return
    }

    Alert.alert(
      'Abmelden',
      'Bist du sicher, dass du dich abmelden möchtest?',
      [
        { text: 'Abbrechen', onPress: () => {}, style: 'cancel' },
        {
          text: 'Abmelden',
          onPress: () => dispatch(ActionCreators.logoutUser()),
        },
      ],
      { cancelable: false }
    )
  }

  const renderPatientGroupImage = () => {
    const group = patientGroups.find((i) => i.id === patientGroup)

    return patientGroup ? (
      <View style={styles.centeredContent}>
        <CrossPlatformImage
          source={group?.image}
          source2x={group?.image2x}
          source3x={group?.image3x}
        />
      </View>
    ) : null
  }

  const handleRequestVerify = async () => {
    setLoading(true)
    try {
      await dispatch(ActionCreators.requestVerifyEmail())
    } catch (reason) {
      setError(reason)
    } finally {
      setHasRequestedVerify(true)
      setLoading(false)
    }
  }

  const patientGroupIndex = patientGroups.findIndex(
    (v) => v.id === patientGroup
  )

  const sectionData = [
    {
      title: 'Patientengruppe',
      data: [
        {
          key: 'custom3456346523',
          type: 'custom',
          onPress: () => {},
          content: (
            <>
              <SegmentedControlTab
                values={patientGroups
                  .filter((p) => p.enabled)
                  .map((p) => p.label)}
                // values={['Kinder', 'Erwachsene', 'Pflegebedürftige']}
                selectedIndex={patientGroupIndex}
                onTabPress={(index) => handleIndexChange(index)}
                // style
                borderRadius={0}
                tabStyle={styles.patientGroupTabStyle}
                tabTextStyle={styles.patientGroupTextStyle}
                tabsContainerStyle={styles.patientGroupContainerStyle}
                activeTabStyle={styles.patientGroupActiveTabStyle}
                activeTabTextStyle={styles.patientGroupActiveTabTextStyle}
              />

              {renderPatientGroupImage()}

              {process.env.MODE !== 'PRODUCTION' && (
                <HGButton
                  title="Patientengruppe zurücksetzen"
                  type="inverted"
                  size="small"
                  onPress={() => {
                    dispatch(
                      ActionCreators.setPatientGroup({ patientGroup: null })
                    )
                    dispatch(
                      ActionCreators.togglePatientGroupModal({ toggle: true })
                    )
                  }}
                />
              )}
            </>
          ),
        },
      ],
    },
    {
      title: 'Profil',
      data: [
        {
          key: 'custom058738582035',
          type: 'custom',
          content: function EmailNotVerifiedWarning() {
            if (emailVerified || hideInfoMessages) return null

            return (
              <View style={tw('rounded bg-yellow-50 px-5 py-3 mb-3 flex')}>
                {error && <HGText>⚠️ {error.message}</HGText>}

                {!hasRequestedVerify && !error && (
                  <>
                    <HGText style={tw('mb-3')}>
                      Bitte bestätigen Sie Ihre E-Mail-Adresse{' '}
                      <HGText bold>{email}</HGText>.
                    </HGText>

                    <HGButton
                      title={loading ? 'Bitte warten…' : 'E-Mail erneut senden'}
                      disabled={loading}
                      size="small"
                      // type="inverted"
                      style={tw('self-start')}
                      onPress={() => handleRequestVerify()}
                    />
                  </>
                )}

                {hasRequestedVerify && !error && (
                  <HGText>
                    Bitte prüfen Sie Ihr E-Mail-Postfach und folgenden Sie den
                    Anweisungen.
                  </HGText>
                )}
              </View>
            )
          },
        },
        {
          key: 'userchangeemail',
          title: 'E-Mail-Adresse ändern',
          onPress: () => navigation.navigate('UserChangeEmail'),
        },
        {
          key: 'userchangepassword',
          title: 'Passwort ändern',
          onPress: () => navigation.navigate('UserChangePassword'),
        },
      ],
    },
    {
      title: 'Rechtliches',
      data: [
        {
          key: 'contact',
          title: 'Kontakt',
          onPress: () => Linking.openURL(`${process.env.URL_BASE}kontakt`),
        },
        {
          key: 'privacy',
          title: 'Datenschutzerklärung',
          onPress: () => Linking.openURL(`${process.env.URL_BASE}datenschutz`),
        },
        {
          key: 'imprint',
          title: 'Impressum',
          onPress: () => Linking.openURL(`${process.env.URL_BASE}impressum`),
        },
      ],
    },
  ]

  useFocusEffect(
    React.useCallback(() => {
      return () => {
        if (hasRequestedVerify) {
          setHideInfoMessages(true)
        }
      }
    }, [])
  )

  return (
    <SafeAreaView style={{ flex: 1 }}>
      <HGView>
        <HGSectionList
          style={styles.container}
          contentContainerStyle={tw('px-3')}
          sections={sectionData}
          keyExtractor={(item, index) => item + index}
          ListHeaderComponent={
            <View>
              <HGHeading>Einstellungen</HGHeading>
            </View>
          }
          ListFooterComponent={
            <View style={styles.actionBox}>
              <HGButton title="Ausloggen" onPress={() => handleLogout()} />

              <VersionInfo />
            </View>
          }
        />
      </HGView>
    </SafeAreaView>
  )
}

export default SettingsOverviewScreen
